import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ScrollToTopButton from '@/components/base/ScrollToTopButton';
import ComicCard from '@/components/feature/ComicCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { comics, categories } from '@/mocks/comics';

export default function Home() {
  const hotComics = comics.sort((a, b) => b.views - a.views).slice(0, 8);
  const latestComics = [...comics].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  ).slice(0, 8);

  return (
    <>
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              name: '韩漫网',
              url: 'https://www.manhwa-read.com/',
              description: '韩漫网站提供免费漫画大全，覆盖浪漫爱情、奇幻冒险、热血动作等多种题材。高清画质流畅阅读，随时随地畅享韩国漫画的精彩世界。',
              contactPoint: {
                '@type': 'ContactPoint',
                email: 'contact@manhwa-read.com',
                contactType: 'customer service',
                availableLanguage: ['Chinese'],
              },
            },
            {
              '@type': 'WebSite',
              name: '韩漫网',
              url: 'https://www.manhwa-read.com/',
              description: '韩漫网站 - 免费漫画大全 - 韩国漫画在线阅读',
              inLanguage: 'zh-CN',
              potentialAction: {
                '@type': 'SearchAction',
                target: 'https://www.manhwa-read.com/search?q={search_term_string}',
                'query-input': 'required name=search_term_string',
              },
            },
            {
              '@type': 'WebPage',
              name: '韩漫网 - 首页',
              url: 'https://www.manhwa-read.com/',
              description: '韩漫网站提供免费漫画大全，通过简单易记的韩漫网址直接在线浏览各种韩国漫画，享受便捷高清的阅读体验。',
              inLanguage: 'zh-CN',
              isPartOf: { '@id': 'https://www.manhwa-read.com/#website' },
            },
          ],
        }}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20stylized%20artistic%20Korean%20manhwa%20themed%20illustration%20with%20warm%20gradients%2C%20abstract%20comic%20panel%20elements%2C%20soft%20atmospheric%20lighting%2C%20dreamy%20ethereal%20vibe%2C%20cinematic%20composition%2C%20warm%20orange%20and%20amber%20tones%2C%20subtle%20texture%2C%20no%20text&width=1800&height=900&seq=hero-bg-manhwa&orientation=landscape"
            alt="韩漫网 - 免费韩国漫画在线阅读"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold font-heading text-white mb-4 leading-tight">
              发现最精彩的
              <span className="text-[oklch(var(--accent-500))]">韩国漫画</span>
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              韩漫网为您提供海量免费韩国漫画资源，覆盖浪漫爱情、奇幻冒险、热血动作等多种题材。
              高清画质、流畅阅读，随时随地畅享韩漫的精彩世界。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/romance"
                className="inline-flex items-center gap-2 bg-[oklch(var(--primary-500))] text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[oklch(var(--primary-600))] transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-read-line" />
                开始阅读
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white border border-white/30 px-6 py-3 rounded-full text-sm font-medium hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-information-line" />
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-2">
                漫画分类
              </h2>
              <p className="text-sm text-foreground-600">
                探索不同类型的韩国漫画，找到你的最爱
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-3 p-5 rounded-lg bg-background-100 hover:bg-[oklch(var(--primary-100)_/_0.5)] transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 group-hover:bg-[oklch(var(--primary-200)_/_0.5)] transition-all">
                    <i className={`${cat.icon} text-xl text-foreground-700 group-hover:text-primary-500 transition-colors`} />
                  </div>
                  <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Comics Section */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-1">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-600">最受读者欢迎的韩国漫画</p>
              </div>
              <Link
                to="/category/romance"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: 'ri-book-open-line', value: '10,000+', label: '漫画资源' },
                { icon: 'ri-user-heart-line', value: '500万+', label: '活跃读者' },
                { icon: 'ri-sparkling-line', value: '99.9%', label: '高清画质' },
                { icon: 'ri-thumb-up-line', value: '98%', label: '好评率' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 flex items-center justify-center rounded-full bg-[oklch(var(--accent-100))]">
                    <i className={`${stat.icon} text-xl md:text-2xl text-[oklch(var(--accent-500))]`} />
                  </div>
                  <div className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-600 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-1">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-600">刚刚更新的漫画章节</p>
              </div>
              <Link
                to="/category/action"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {latestComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-2">
                为什么选择韩漫网？
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                我们致力于为漫画爱好者提供最优质的阅读体验
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-smartphone-line',
                  title: '多端适配',
                  desc: 'PC、平板、手机完美适配，随时随地畅享阅读乐趣',
                },
                {
                  icon: 'ri-image-line',
                  title: '高清画质',
                  desc: '所有漫画均为高清扫描版本，细节清晰，色彩还原度极高',
                },
                {
                  icon: 'ri-flashlight-line',
                  title: '极速加载',
                  desc: '采用先进图片压缩技术，确保漫画页面秒开，阅读不卡顿',
                },
                {
                  icon: 'ri-bookmark-line',
                  title: '阅读记录',
                  desc: '自动保存阅读进度，下次打开直接继续，不错过任何精彩',
                },
                {
                  icon: 'ri-global-line',
                  title: '海量资源',
                  desc: '收录超过一万部韩国漫画，热门冷门应有尽有，每日持续更新',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '完全免费',
                  desc: '所有漫画均可免费阅读，无需付费订阅，永久免费承诺',
                },
              ].map((item) => (
                <div key={item.title} className="p-6 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[oklch(var(--accent-100))] mb-4">
                    <i className={`${item.icon} text-lg text-[oklch(var(--accent-500))]`} />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-4 md:px-6 py-14 md:py-20 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20atmospheric%20Korean%20manhwa%20style%20illustration%2C%20cozy%20reading%20nook%20with%20soft%20lighting%2C%20artistic%20comic%20elements%2C%20dreamy%20atmosphere%2C%20warm%20amber%20and%20golden%20tones%2C%20cinematic%20composition%2C%20no%20text&width=1800&height=600&seq=cta-manhwa&orientation=landscape"
            alt="开始阅读"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
          <div className="relative z-10 max-w-7xl mx-auto text-center">
            <h2 className="text-xl md:text-3xl font-bold font-heading text-white mb-3">
              准备好了吗？开始你的韩漫之旅
            </h2>
            <p className="text-sm md:text-base text-white/80 mb-6 max-w-lg mx-auto">
              加入数百万读者的行列，探索最精彩的韩国漫画世界。完全免费，无需注册，即刻开始阅读！
            </p>
            <Link
              to="/category/romance"
              className="inline-flex items-center gap-2 bg-[oklch(var(--accent-500))] text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-[oklch(var(--accent-600))] transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line" />
              立即开始阅读
            </Link>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTopButton />
    </>
  );
}